import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { saveAdditionalNotesTemplate, updateAdditionalNotesTemplate } from "@/lib/stores/additionalNotesTemplates";
import { useAuthStore } from "@/lib/auth";
import { getDoctorIdForUser, ensureDoctorProfile } from "@/lib/utils/doctorUtils";

interface SaveAdditionalNotesTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  notes: string;
  onTemplateSaved?: () => void;
  templateId?: string; // if provided, update instead of create
  initialDisease?: string;
  initialTemplateName?: string;
}

export function SaveAdditionalNotesTemplateDialog({
  isOpen,
  onClose,
  notes,
  onTemplateSaved,
  templateId,
  initialDisease = "",
  initialTemplateName = ""
}: SaveAdditionalNotesTemplateDialogProps) {
  const { toast } = useToast();
  const { user } = useAuthStore();
  const [disease, setDisease] = useState(initialDisease);
  const [templateName, setTemplateName] = useState(initialTemplateName);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setDisease(initialDisease || "");
    setTemplateName(initialTemplateName || "");
  }, [isOpen, initialDisease, initialTemplateName]);

  const handleSave = async () => {
    if (!disease.trim()) {
      toast({
        title: "Disease required",
        description: "Please enter a disease for the template",
        variant: "destructive"
      });
      return;
    }

    if (!templateName.trim()) {
      toast({
        title: "Template name required",
        description: "Please enter a name for the template",
        variant: "destructive"
      });
      return;
    }

    if (!notes.trim()) {
      toast({
        title: "Notes required",
        description: "Please enter some notes to save as template",
        variant: "destructive"
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: "Authentication error",
        description: "User ID not found",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    try {
      // Get or create doctor profile for the user
      let doctorId = await getDoctorIdForUser(user);
      
      if (!doctorId) {
        console.log('No doctor profile found, creating one...');
        doctorId = await ensureDoctorProfile(user);
        
        if (!doctorId) {
          toast({
            title: "Error",
            description: "Could not create doctor profile. Please contact administrator.",
            variant: "destructive"
          });
          return;
        }
      }

      console.log('Using doctor ID:', doctorId);

      if (templateId) {
        await updateAdditionalNotesTemplate({
          id: templateId,
          doctorId: doctorId,
          disease: disease.trim(),
          templateName: templateName.trim(),
          notes: notes.trim()
        });
      } else {
        await saveAdditionalNotesTemplate({
          doctorId: doctorId,
          disease: disease.trim(),
          templateName: templateName.trim(),
          notes: notes.trim()
        });
      }

      toast({
        title: "Template saved",
        description: `Template "${templateName}" saved successfully for ${disease}`
      });

      // Reset form
      setDisease("");
      setTemplateName("");
      onTemplateSaved?.();
      onClose();
    } catch (error) {
      console.error("Error saving template:", error);
      toast({
        title: "Error saving template",
        description: error instanceof Error ? error.message : "Failed to save template. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setDisease("");
    setTemplateName("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
      <DialogTitle>{templateId ? 'Edit Notes Template' : 'Save Notes as Template'}</DialogTitle>
          <DialogDescription>
            Save your current additional notes as a reusable template for future prescriptions with similar diseases.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left side - Disease and Template Name */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="disease">Disease/Condition</Label>
              <Input
                id="disease"
                value={disease}
                onChange={(e) => setDisease(e.target.value)}
                placeholder="e.g., ASOM, SINUSITIS, ADULT ACUTE TONSILLITIS"
                className="w-full"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Standard ASOM Notes"
                className="w-full"
              />
            </div>

            <div className="text-sm text-muted-foreground">
              <p>This template will be saved to your personal template library and can be used for future patients with similar diseases.</p>
            </div>
          </div>

          {/* Right side - Notes Preview */}
          <div className="space-y-4">
            <Label>Notes to Save</Label>
            <div className="border rounded-md p-4 max-h-96 overflow-y-auto">
              {!notes.trim() ? (
                <p className="text-sm text-muted-foreground">No notes to save</p>
              ) : (
                <div className="whitespace-pre-wrap text-sm">
                  {notes}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving || !notes.trim()}
            className="min-w-[120px]"
          >
            {isSaving ? "Saving..." : "Save Template"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
